import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { withEllipsis } from "../../utils/textSymbols";
import { FIPS_TO_CODE, STATE_NAMES, TERRITORY_CODES, CANADA_PROVINCE_CODES, OTHER_REGION_CODES } from "../../data/licensePlateStates.js";
import StateCelebration from "./StateCelebration";
import CelebrationConfetti from "./CelebrationConfetti";
import { CELEBRATION_DURATION_MS } from "./celebrationTiming";

const GEO_URL = `${process.env.PUBLIC_URL || ""}/us-states-10m.json`;
const UNCLAIMED = "#e8e8e8";
const UNCLAIMED_DARK = "#2a2a2a";
const STROKE = "#333";
const STROKE_DARK = "#666";
const VIEWBOX = "0 0 975 610";
const MIN_SCALE = 0.5;
const MAX_SCALE = 8;
const DRAG_THRESHOLD = 8;
const PINCH_MOVE_THRESHOLD = 6;
const SUPPRESS_SELECT_MS = 400;
const DC_COORDS = [-77.0369, 38.9072];
const DC_STAR_OUTER = 16;
const DC_STAR_INNER = 7;
const DC_STAR_HALO = 2.4;

function buildStarPath(cx, cy, outerRadius, innerRadius, points = 5) {
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return "";
  let d = "";
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i += 1) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = i * step - Math.PI / 2;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    d += `${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)} `;
  }
  return `${d}Z`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function pointerDistance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export default function USMap({
  claims = {},
  memberColors = {},
  onStateClick,
  celebration = null,
  onCelebrationComplete,
  dark = false,
  showOther = true,
  claimerIdKey = "spottedBy"
}) {
  const [shapes, setShapes] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverCode, setHoverCode] = useState(null);
  const [otherOpen, setOtherOpen] = useState(false);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [celebrationOrigin, setCelebrationOrigin] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(400);
  const [dcMarker, setDcMarker] = useState(null);

  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const transformRef = useRef(transform);
  const dragRef = useRef({
    active: false,
    moved: false,
    captured: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    pointerId: null
  });
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const didPinchRef = useRef(false);
  const suppressSelectUntilRef = useRef(0);
  const pointerTargetStateRef = useRef(null);

  const unclaimedFill = dark ? UNCLAIMED_DARK : UNCLAIMED;
  const strokeColor = dark ? STROKE_DARK : STROKE;
  const hoverFill = dark ? "#3a5070" : "#cce0ff";

  transformRef.current = transform;

  const dcStarPath = useMemo(() => {
    if (!dcMarker) return "";
    return buildStarPath(dcMarker.cx, dcMarker.cy, DC_STAR_OUTER, DC_STAR_INNER);
  }, [dcMarker]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    fetch(GEO_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Map data failed (${res.status})`);
        return res.json();
      })
      .then((topology) => {
        if (cancelled) return;
        const collection = feature(topology, topology.objects.states);
        const projection = geoAlbersUsa().scale(1300).translate([487.5, 305]);
        const pathGen = geoPath(projection);
        const items = collection.features
          .map((f) => {
            const code = FIPS_TO_CODE[String(f.id).padStart(2, "0")];
            // DC is too small on this map; render as a dedicated star marker instead.
            if (!code || code === "DC") return null;
            const d = pathGen(f);
            if (!d) return null;
            const [cx, cy] = pathGen.centroid(f);
            return { code, d, cx, cy };
          })
          .filter(Boolean);
        const dcPoint = projection(DC_COORDS);
        setShapes(items);
        setDcMarker(dcPoint ? { cx: dcPoint[0], cy: dcPoint[1] } : null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const colorForCode = useMemo(() => (code) => {
    const claim = claims[code];
    if (!claim) return unclaimedFill;
    const claimerId = claim[claimerIdKey] || claim.spottedBy || claim.foundBy;
    return memberColors[claimerId] || "#2d74da";
  }, [claims, memberColors, unclaimedFill, claimerIdKey]);

  const getSvgPoint = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }, []);

  const applyZoomAt = useCallback((clientX, clientY, nextK) => {
    const { x: px, y: py } = getSvgPoint(clientX, clientY);
    setTransform((prev) => {
      const k = clamp(nextK, MIN_SCALE, MAX_SCALE);
      const ratio = k / prev.k;
      return {
        k,
        x: px - ratio * (px - prev.x),
        y: py - ratio * (py - prev.y)
      };
    });
  }, [getSvgPoint]);

  const zoomByFactor = useCallback((clientX, clientY, factor) => {
    const prev = transformRef.current;
    applyZoomAt(clientX, clientY, prev.k * factor);
  }, [applyZoomAt]);

  const endGesture = useCallback(() => {
    const drag = dragRef.current;
    drag.active = false;
    drag.moved = false;
    drag.captured = false;
    drag.pointerId = null;
    pinchRef.current = null;
    didPinchRef.current = false;
    pointersRef.current.clear();
  }, []);

  const shouldSuppressSelect = useCallback(() => {
    if (Date.now() < suppressSelectUntilRef.current) return true;
    if (didPinchRef.current) return true;
    if (pointersRef.current.size > 0) return true;
    if (pinchRef.current) return true;
    return false;
  }, []);

  const swallowNextClick = useCallback(() => {
    if (typeof window === "undefined") return;
    const swallow = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", swallow, true);
    };
    window.addEventListener("click", swallow, true);
    setTimeout(() => {
      window.removeEventListener("click", swallow, true);
    }, 500);
  }, []);

  const trySelectState = useCallback((code, e) => {
    const drag = dragRef.current;
    if (drag.moved || shouldSuppressSelect()) return;
    if (e?.pointerId != null && drag.pointerId != null && drag.pointerId !== e.pointerId) return;
    if (e?.pointerType && e.pointerType !== "mouse") {
      swallowNextClick();
    }
    onStateClick?.(code);
  }, [onStateClick, shouldSuppressSelect, swallowNextClick]);

  const releaseDragPointer = useCallback((e) => {
    const drag = dragRef.current;
    if (drag.pointerId !== e.pointerId) return;
    if (drag.captured) {
      try {
        svgRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
    drag.active = false;
    drag.captured = false;
    drag.pointerId = null;
    drag.moved = false;
  }, []);

  const stateCodeFromTarget = useCallback((target) => {
    const el = target?.closest?.("[data-state-code]");
    return el?.getAttribute("data-state-code") || null;
  }, []);

  const trackPointerUp = useCallback((e) => {
    const wasPinching = pointersRef.current.size >= 2 || Boolean(pinchRef.current);
    const hadPinchZoom = didPinchRef.current;

    pointersRef.current.delete(e.pointerId);

    if (pointersRef.current.size < 2) {
      pinchRef.current = null;
    }

    releaseDragPointer(e);

    if (pointersRef.current.size === 0) {
      pinchRef.current = null;
      if (wasPinching || hadPinchZoom) {
        suppressSelectUntilRef.current = Date.now() + SUPPRESS_SELECT_MS;
      }
      didPinchRef.current = false;
    }

    return { wasPinching, hadPinchZoom };
  }, [releaseDragPointer]);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;

    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2) {
      const pts = [...pointersRef.current.values()];
      const dist = pointerDistance(pts[0], pts[1]);
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      pinchRef.current = {
        startDist: dist,
        startK: transformRef.current.k,
        midX,
        midY
      };
      const drag = dragRef.current;
      if (drag.captured && drag.pointerId != null) {
        try {
          svgRef.current?.releasePointerCapture(drag.pointerId);
        } catch {
          // ignore
        }
      }
      drag.active = false;
      drag.captured = false;
      drag.pointerId = null;
      pointerTargetStateRef.current = null;
      return;
    }

    if (pointersRef.current.size > 2) return;

    pointerTargetStateRef.current = stateCodeFromTarget(e.target);

    dragRef.current = {
      active: true,
      moved: false,
      captured: false,
      startX: e.clientX,
      startY: e.clientY,
      originX: transformRef.current.x,
      originY: transformRef.current.y,
      pointerId: e.pointerId
    };

    try {
      svgRef.current?.setPointerCapture(e.pointerId);
      dragRef.current.captured = true;
    } catch {
      // ignore
    }
  }, [stateCodeFromTarget]);

  const onPointerMove = useCallback((e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2 && pinchRef.current) {
      const pts = [...pointersRef.current.values()];
      const dist = pointerDistance(pts[0], pts[1]);
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      const { startDist, startK } = pinchRef.current;
      if (startDist > 0 && Math.abs(dist - startDist) >= PINCH_MOVE_THRESHOLD) {
        didPinchRef.current = true;
      }
      if (startDist > 0) {
        applyZoomAt(midX, midY, startK * (dist / startDist));
      }
      pinchRef.current.midX = midX;
      pinchRef.current.midY = midY;
      return;
    }

    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    if (!drag.moved && Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
      drag.moved = true;
      pointerTargetStateRef.current = null;
    }

    if (drag.moved) {
      const svg = svgRef.current;
      const scale = svg?.getScreenCTM()?.a || 1;
      setTransform((prev) => ({
        ...prev,
        x: drag.originX + dx / scale,
        y: drag.originY + dy / scale
      }));
    }
  }, [applyZoomAt]);

  const onPointerUp = useCallback((e) => {
    const wasMoved = dragRef.current.moved;
    const targetState = pointerTargetStateRef.current;
    const { wasPinching, hadPinchZoom } = trackPointerUp(e);

    if (pointersRef.current.size === 0) {
      pointerTargetStateRef.current = null;
    }

    if (!wasMoved && !wasPinching && !hadPinchZoom && targetState) {
      trySelectState(targetState, e);
    }
  }, [trackPointerUp, trySelectState]);

  const onPointerCancel = useCallback((e) => {
    onPointerUp(e);
    if (pointersRef.current.size === 0) endGesture();
  }, [onPointerUp, endGesture]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      zoomByFactor(e.clientX, e.clientY, factor);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomByFactor]);

  const handleCelebrationComplete = useCallback(() => {
    onCelebrationComplete?.();
  }, [onCelebrationComplete]);

  const celebratingShape = useMemo(() => {
    if (!celebration?.code) return null;
    if (celebration.code === "DC" && dcMarker) return dcMarker;
    return shapes.find((s) => s.code === celebration.code) || null;
  }, [celebration?.code, dcMarker, shapes]);

  const isAnimating = Boolean(celebration?.code && celebratingShape);

  const updateCelebrationOrigin = useCallback(() => {
    const svg = svgRef.current;
    const viewport = viewportRef.current;
    if (!svg || !viewport || !celebratingShape) {
      setCelebrationOrigin(null);
      return;
    }

    const { cx, cy } = celebratingShape;
    const pt = svg.createSVGPoint();
    pt.x = cx * transform.k + transform.x;
    pt.y = cy * transform.k + transform.y;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const screen = pt.matrixTransform(ctm);
    const viewportRect = viewport.getBoundingClientRect();
    setCelebrationOrigin({
      left: screen.x - viewportRect.left,
      top: screen.y - viewportRect.top
    });
    setViewportHeight(viewport.clientHeight || 400);
  }, [celebratingShape, transform]);

  useLayoutEffect(() => {
    if (!isAnimating) {
      setCelebrationOrigin(null);
      return;
    }
    updateCelebrationOrigin();
  }, [isAnimating, celebration?.code, updateCelebrationOrigin]);

  useEffect(() => {
    if (!isAnimating) return undefined;
    const timer = setTimeout(() => {
      handleCelebrationComplete();
    }, CELEBRATION_DURATION_MS);
    return () => clearTimeout(timer);
  }, [isAnimating, handleCelebrationComplete]);

  const zoomFromCenter = useCallback((factor) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    zoomByFactor(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
  }, [zoomByFactor]);

  const renderOtherRegionItem = (code) => {
    const claimed = !!claims[code];
    const isCelebrating = isAnimating && celebration?.code === code;
    const fill = isCelebrating ? unclaimedFill : colorForCode(code);
    return (
      <li key={code}>
        <button
          type="button"
          className={`lp-other-item ${claimed && !isCelebrating ? "claimed" : ""} ${isCelebrating ? "lp-territory-celebrating" : ""}`}
          style={{
            backgroundColor: fill,
            borderColor: strokeColor,
            "--lp-celebrate-fill": isCelebrating ? (celebration.color || "#2d74da") : undefined
          }}
          onClick={() => {
            if (!isCelebrating && onStateClick) {
              setOtherOpen(false);
              onStateClick(code);
            }
          }}
        >
          <span className="lp-other-item-code">{code}</span>
          <span className="lp-other-item-name">{STATE_NAMES[code]}</span>
        </button>
      </li>
    );
  };

  if (loading) {
    return <p className="lp-status">{withEllipsis("Loading map")}</p>;
  }
  if (loadError) {
    return <p className="lp-error">Could not load map: {loadError}</p>;
  }

  return (
    <div className="lp-map-wrap">
      <div className="lp-map-viewport" ref={viewportRef}>
        <svg
          ref={svgRef}
          viewBox={VIEWBOX}
          className="lp-map"
          role="img"
          aria-label="United States map"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
            {shapes.map(({ code, d, cx, cy }) => {
              const claimed = !!claims[code];
              const isCelebrating = isAnimating && celebration.code === code;
              const hideClaim = isCelebrating;
              const fill = hideClaim ? unclaimedFill : colorForCode(code);
              const isHover = hoverCode === code;
              return (
                <path
                  key={code}
                  data-state-code={code}
                  d={d}
                  fill={isHover && !claimed && !hideClaim ? hoverFill : fill}
                  stroke={strokeColor}
                  strokeWidth={isHover ? 1.2 : 0.6}
                  className={isCelebrating ? "lp-state-celebrating" : undefined}
                  style={{
                    cursor: "pointer",
                    transition: hideClaim ? "none" : "fill 0.35s ease",
                    "--lp-celebrate-fill": isCelebrating ? (celebration.color || "#2d74da") : undefined
                  }}
                  aria-label={STATE_NAMES[code] || code}
                  onMouseEnter={() => setHoverCode(code)}
                  onMouseLeave={() => setHoverCode(null)}
                />
              );
            })}
            {dcMarker && dcStarPath && (() => {
              const claimed = !!claims.DC;
              const isCelebrating = isAnimating && celebration?.code === "DC";
              const hideClaim = isCelebrating;
              const fill = hideClaim ? unclaimedFill : colorForCode("DC");
              const isHover = hoverCode === "DC";
              const starFill = isHover && !claimed && !hideClaim ? hoverFill : fill;
              return (
                <g
                  data-state-code="DC"
                  style={{ cursor: "pointer" }}
                  aria-label={STATE_NAMES.DC || "District of Columbia"}
                  onMouseEnter={() => setHoverCode("DC")}
                  onMouseLeave={() => setHoverCode(null)}
                >
                  <circle
                    cx={dcMarker.cx}
                    cy={dcMarker.cy}
                    r={DC_STAR_OUTER + DC_STAR_HALO}
                    fill={dark ? "#111" : "#fff"}
                    stroke={strokeColor}
                    strokeWidth={1}
                    pointerEvents="none"
                  />
                  <path
                    d={dcStarPath}
                    fill={starFill}
                    stroke={strokeColor}
                    strokeWidth={isHover ? 1.6 : 1.2}
                    className={isCelebrating ? "lp-state-celebrating" : undefined}
                    style={{
                      transition: hideClaim ? "none" : "fill 0.35s ease",
                      "--lp-celebrate-fill": isCelebrating ? (celebration.color || "#2d74da") : undefined
                    }}
                  />
                  <text
                    x={dcMarker.cx + DC_STAR_OUTER + 4}
                    y={dcMarker.cy + 4}
                    fontSize="11"
                    fontWeight="800"
                    fill={dark ? "#e0e0e0" : "#222"}
                    pointerEvents="none"
                  >
                    DC
                  </text>
                </g>
              );
            })()}
            {isAnimating && (
              <StateCelebration
                cx={celebratingShape.cx}
                cy={celebratingShape.cy}
                color={celebration.color || "#2d74da"}
              />
            )}
          </g>
        </svg>
        {isAnimating && celebrationOrigin && (
          <CelebrationConfetti
            left={celebrationOrigin.left}
            top={celebrationOrigin.top}
            color={celebration.color || "#2d74da"}
            viewportHeight={viewportHeight}
          />
        )}
        <div className="lp-map-zoom-controls" aria-label="Map zoom">
          <button type="button" className="lp-map-zoom-btn" onClick={() => zoomFromCenter(1.25)} aria-label="Zoom in">+</button>
          <button type="button" className="lp-map-zoom-btn" onClick={() => zoomFromCenter(1 / 1.25)} aria-label="Zoom out">−</button>
        </div>
        <div className="lp-map-zoom-hint">Pinch or scroll to zoom · drag to pan</div>
      </div>
      {showOther && (
        <div className="lp-territories">
          <button
            type="button"
            className="lp-other-btn"
            onClick={() => setOtherOpen(true)}
            aria-haspopup="dialog"
          >
            Other
            <span className="lp-other-sub">
              {OTHER_REGION_CODES.filter((c) => claims[c]).length}/{OTHER_REGION_CODES.length} claimed
            </span>
          </button>
        </div>
      )}

      {showOther && otherOpen && (
        <div className="lp-modal-backdrop" onClick={() => setOtherOpen(false)}>
          <div className="lp-modal lp-other-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Territories, Canada, and other regions">
            <h2 className="lp-modal-title">Other regions</h2>
            <p className="lp-modal-sub">US territories and Canadian provinces not shown on the map</p>
            <h3 className="lp-other-section-title">US territories</h3>
            <ul className="lp-other-list">
              {TERRITORY_CODES.map(renderOtherRegionItem)}
            </ul>
            <h3 className="lp-other-section-title">Canadian provinces</h3>
            <ul className="lp-other-list">
              {CANADA_PROVINCE_CODES.map(renderOtherRegionItem)}
            </ul>
            <button type="button" className="lp-cancel-btn" onClick={() => setOtherOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
